import { useEffect, useState, useMemo } from 'react';
import { ppeIssuesAPI, employeesAPI, departmentsAPI } from '../lib/api';
import { formatDate, cn, downloadBlob } from '../lib/utils';
import { HardHat, AlertTriangle, Clock, Package, Users, Search, Download, FileSpreadsheet, Eye, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#6366F1', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

export default function PPEDashboard() {
  const [stats, setStats] = useState(null);
  const [overdue, setOverdue] = useState([]);
  const [dueSoon, setDueSoon] = useState([]);
  const [issues, setIssues] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [reasonStats, setReasonStats] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Memoised lost/damaged/worn-out subset to avoid re-filtering on each render.
  const lostDamagedReasons = useMemo(
    () => reasonStats.filter(r => ['Lost', 'Damaged', 'Worn out'].includes(r._id)),
    [reasonStats]
  );

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, overdueRes, dueSoonRes, issuesRes, deptRes, empRes, reasonRes] = await Promise.all([
        ppeIssuesAPI.getStats(),
        ppeIssuesAPI.getOverdue(),
        ppeIssuesAPI.getDueSoon(30),
        ppeIssuesAPI.getAll({}),
        departmentsAPI.getAll(),
        employeesAPI.getAll({ status: 'Active' }),
        ppeIssuesAPI.getReportByReason(),
      ]);
      
      setStats(statsRes.data);
      setOverdue(overdueRes.data);
      setDueSoon(dueSoonRes.data);
      setIssues(issuesRes.data);
      setDepartments(deptRes.data);
      setEmployees(empRes.data);
      setReasonStats(reasonRes.data);
    } catch (error) {
      toast.error('Failed to load PPE dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleExportByEmployee = async () => {
    try {
      const res = await ppeIssuesAPI.getReportByEmployeeExcel({});
      downloadBlob(res.data, 'ppe_by_employee.xlsx');
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const handleExportByDepartment = async () => {
    try {
      const res = await ppeIssuesAPI.getReportByDepartmentExcel();
      downloadBlob(res.data, 'ppe_by_department.xlsx');
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const filteredIssues = issues.filter(issue => {
    if (filterEmployee !== 'all' && issue.employee_id !== filterEmployee) return false;
    if (filterDepartment !== 'all' && issue.department !== filterDepartment) return false;
    if (filterStartDate && issue.issue_date < filterStartDate) return false;
    if (filterEndDate && issue.issue_date > filterEndDate) return false;
    return true;
  });

  const reasonChartData = reasonStats.map((r, i) => ({
    name: r._id || 'Unknown',
    value: r.count,
    color: COLORS[i % COLORS.length]
  }));

  return (
    <div className="space-y-6 page-transition" data-testid="ppe-dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-3">
            <HardHat className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
            PPE Management
          </h1>
          <p className="text-muted-foreground mt-1">Track and manage personal protective equipment</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportByEmployee}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            By Employee
          </Button>
          <Button variant="outline" onClick={handleExportByDepartment}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            By Department
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          icon={Package}
          label="PPE Items"
          value={stats?.total_ppe_items || 0}
          color="text-indigo-400"
          bgColor="bg-indigo-500/20"
        />
        <StatCard
          icon={HardHat}
          label="Total Issues"
          value={stats?.total_issues || 0}
          color="text-emerald-400"
          bgColor="bg-emerald-500/20"
        />
        <StatCard
          icon={Clock}
          label="This Month"
          value={stats?.issues_this_month || 0}
          color="text-blue-400"
          bgColor="bg-blue-500/20"
        />
        <StatCard
          icon={AlertTriangle}
          label="Overdue"
          value={stats?.overdue_replacements || 0}
          color="text-red-400"
          bgColor="bg-red-500/20"
          highlight={stats?.overdue_replacements > 0}
        />
        <StatCard
          icon={Clock}
          label="Due in 30 Days"
          value={stats?.due_within_30_days || 0}
          color="text-amber-400"
          bgColor="bg-amber-500/20"
          highlight={stats?.due_within_30_days > 0}
        />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="issues" className="space-y-6">
        <TabsList className="bg-secondary/30 w-full justify-start overflow-x-auto flex-nowrap">
          <TabsTrigger value="issues">All Issues</TabsTrigger>
          <TabsTrigger value="overdue" className="relative">
            Overdue
            {overdue.length > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-red-500 text-white rounded-full">
                {overdue.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="due-soon">Due Soon</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
        </TabsList>

        {/* All Issues Tab */}
        <TabsContent value="issues" className="space-y-4">
          {/* Filters */}
          <div className="glass-card p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder="Employee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Employees</SelectItem>
                  {employees.map((emp) => (
                    <SelectItem key={emp.employee_id} value={emp.employee_id}>
                      {emp.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterDepartment} onValueChange={setFilterDepartment}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder="Department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.name}>{dept.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="date"
                value={filterStartDate}
                onChange={(e) => setFilterStartDate(e.target.value)}
                placeholder="Start date"
                className="w-full md:w-40"
              />
              <Input
                type="date"
                value={filterEndDate}
                onChange={(e) => setFilterEndDate(e.target.value)}
                placeholder="End date"
                className="w-full md:w-40"
              />
              <Button 
                variant="ghost" 
                onClick={() => {
                  setFilterEmployee('all');
                  setFilterDepartment('all');
                  setFilterStartDate('');
                  setFilterEndDate('');
                }}
              >
                Clear
              </Button>
            </div>
          </div>

          <IssuesTable 
            issues={filteredIssues} 
            loading={loading}
            onViewDetail={(issue) => {
              setSelectedIssue(issue);
              setShowDetailModal(true);
            }}
          />
        </TabsContent>

        {/* Overdue Tab */}
        <TabsContent value="overdue">
          <div className="glass-card p-4 mb-4 border-red-500/30 bg-red-500/5">
            <div className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">
                {overdue.length} PPE item(s) are overdue for replacement
              </span>
            </div>
          </div>
          <IssuesTable 
            issues={overdue} 
            loading={loading}
            onViewDetail={(issue) => {
              setSelectedIssue(issue);
              setShowDetailModal(true);
            }}
          />
        </TabsContent>

        {/* Due Soon Tab */}
        <TabsContent value="due-soon">
          <div className="glass-card p-4 mb-4 border-amber-500/30 bg-amber-500/5">
            <div className="flex items-center gap-2 text-amber-400">
              <Clock className="w-5 h-5" />
              <span className="font-medium">
                {dueSoon.length} PPE item(s) due for replacement in the next 30 days
              </span>
            </div>
          </div>
          <IssuesTable 
            issues={dueSoon} 
            loading={loading}
            onViewDetail={(issue) => {
              setSelectedIssue(issue);
              setShowDetailModal(true);
            }}
          />
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Issues by Reason Chart */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold mb-4">Issues by Reason</h3>
              {reasonChartData.length > 0 ? (
                <div className="flex items-center gap-6">
                  <div className="w-48 h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={reasonChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={70}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {reasonChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1E293B', 
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '8px'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex-1 space-y-2">
                    {reasonChartData.map((item) => (
                      <div key={item.name} className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="text-sm text-muted-foreground flex-1">{item.name}</span>
                        <span className="text-sm font-medium">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-muted-foreground">
                  No data available
                </div>
              )}
            </div>

            {/* Lost/Damaged Trend */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold mb-4">Lost & Damaged PPE</h3>
              <div className="space-y-4">
                {lostDamagedReasons.map((item) => (
                    <div key={item._id} className="flex items-center justify-between p-4 rounded-lg bg-secondary/30">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-10 h-10 rounded-lg flex items-center justify-center",
                          item._id === 'Lost' && "bg-red-500/20",
                          item._id === 'Damaged' && "bg-amber-500/20",
                          item._id === 'Worn out' && "bg-slate-500/20"
                        )}>
                          <AlertTriangle className={cn(
                            "w-5 h-5",
                            item._id === 'Lost' && "text-red-400",
                            item._id === 'Damaged' && "text-amber-400",
                            item._id === 'Worn out' && "text-slate-400"
                          )} />
                        </div>
                        <div>
                          <p className="font-medium">{item._id}</p>
                          <p className="text-sm text-muted-foreground">{item.total_quantity} items total</p>
                        </div>
                      </div>
                      <span className="text-2xl font-bold">{item.count}</span>
                    </div>
                  ))}
                {reasonStats.filter(r => ['Lost', 'Damaged', 'Worn out'].includes(r._id)).length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No lost or damaged PPE recorded
                  </div>
                )}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>PPE Issue Details</DialogTitle>
          </DialogHeader>
          
          {selectedIssue && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Employee</p>
                  <p className="font-medium">{selectedIssue.employee_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Department</p>
                  <p className="font-medium">{selectedIssue.department}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Issue Date</p>
                  <p className="font-medium">{formatDate(selectedIssue.issue_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">PPE Item</p>
                  <p className="font-medium">{selectedIssue.ppe_item_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Quantity</p>
                  <p className="font-medium">{selectedIssue.quantity} {selectedIssue.unit_of_issue}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Size</p>
                  <p className="font-medium">{selectedIssue.size || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Reason</p>
                  <Badge variant="outline">{selectedIssue.reason_for_issue}</Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Replacement Due</p>
                  <p className="font-medium">
                    {selectedIssue.expected_replacement_date ? formatDate(selectedIssue.expected_replacement_date) : 'Not set'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Issued By</p>
                  <p className="font-medium">{selectedIssue.issued_by_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Condition</p>
                  <p className="font-medium">{selectedIssue.condition_when_issued}</p>
                </div>
              </div>
              {selectedIssue.notes && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Notes</p>
                  <p className="bg-secondary/30 p-3 rounded-lg">{selectedIssue.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, bgColor, highlight }) {
  return (
    <div className={cn(
      "stat-tile",
      highlight && "ring-2 ring-red-500/50"
    )}>
      <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center mb-3", bgColor)}>
        <Icon className={cn("w-6 h-6", color)} />
      </div>
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold text-foreground mt-1">{value}</p>
    </div>
  );
}

function IssuesTable({ issues, loading, onViewDetail }) {
  if (loading) {
    return (
      <div className="glass-card p-8 text-center text-muted-foreground">
        Loading...
      </div>
    );
  }

  if (issues.length === 0) {
    return (
      <div className="glass-card p-8 text-center text-muted-foreground">
        <HardHat className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>No PPE issues found</p>
      </div>
    );
  }

  const isOverdue = (date) => {
    if (!date) return false;
    return new Date(date) < new Date();
  };

  return (
    <div className="glass-card table-scroll">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>Employee</TableHead>
            <TableHead>Department</TableHead>
            <TableHead>PPE Item</TableHead>
            <TableHead>Qty</TableHead>
            <TableHead>Issue Date</TableHead>
            <TableHead>Replacement Due</TableHead>
            <TableHead>Reason</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {issues.map((issue) => (
            <TableRow key={issue.id} className="table-row-hover">
              <TableCell className="font-medium">{issue.employee_name}</TableCell>
              <TableCell className="text-muted-foreground">{issue.department}</TableCell>
              <TableCell>
                <div>
                  <p>{issue.ppe_item_name}</p>
                  <p className="text-xs text-muted-foreground">{issue.ppe_item_category}</p>
                </div>
              </TableCell>
              <TableCell>{issue.quantity} {issue.unit_of_issue}</TableCell>
              <TableCell className="text-muted-foreground">{formatDate(issue.issue_date)}</TableCell>
              <TableCell>
                {issue.expected_replacement_date ? (
                  <span className={cn(
                    isOverdue(issue.expected_replacement_date) && "text-red-400 font-medium"
                  )}>
                    {formatDate(issue.expected_replacement_date)}
                    {isOverdue(issue.expected_replacement_date) && (
                      <Badge variant="outline" className="ml-2 bg-red-500/20 text-red-400 border-red-500/30 text-xs">
                        Overdue
                      </Badge>
                    )}
                  </span>
                ) : '-'}
              </TableCell>
              <TableCell>
                <Badge variant="outline" className="text-xs">{issue.reason_for_issue}</Badge>
              </TableCell>
              <TableCell className="text-right">
                <Button variant="ghost" size="icon" onClick={() => onViewDetail(issue)}>
                  <Eye className="w-4 h-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
